import { environment } from '@env/environment';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OktaAuthService } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import Utils from '@app/utils/utils';

/*
This service handles Agilent Okta authentication, which is similar to
regular Okta authentication but has an extra server, the Enterprise IT App
server, that "wraps" the Okta server.

Terms:
 - Enterprise IT App    (e.g. dev-register.agilent.com)
 - Okta Server          (e.g.dev-login.agilent.com)

Agilent Okta authentication flow (see authGuardCanActivate function below):

1.  Call Enterprise IT App session API endpoint to determine if
    the user is already logged in to the Enterprise IT App

2.  If the user is NOT already logged in to the Enterprise IT App:

    1.  Generate challenge, nonce, state, verifier values, save them to
        the browser session storage

    2.  Redirect to the Enterprise IT App /login with nonce, state,
        challenge, aid and redirect_uri parameters

3.  Call the Okta Server endpoint to determine if the user is already logged in
    to the Okta Server

4.  If the user if NOT already logged in to the Okta Server:

    1.  Generate challenge, nonce, state, verifier values, save them to
        the browser session storage

    2.  Redirect to the Okta Server /authorize endpoint with nonce, state,
        challenge, and redirect_uri parameters

5.  Otherwise, the user is already logged in to both the Enterprise IT App
    and Okta Server.
*/

interface OktaLoginParams {
    state: string;
    code_challenge: string;
    nonce: string;
    aid: string;
    redirect_uri: string;
}


@Injectable({
    providedIn: 'root'
})
export class AgilentOktaAuthService {

    private oktaAuth: OktaAuth;
    private oktaConfig: any;

    private config = {
        sessionUrl: `${environment.oktaConfig.agilentBaseUrl}/account/agsso/api/v1/agsessionme`,
        loginUrl: `${environment.oktaConfig.agilentBaseUrl}/account/login`,
        logoutUrl: `${environment.oktaConfig.agilentBaseUrl}/account/signout`,
        appId: environment.oktaConfig.agilentAppId
    };

    constructor(
        private oktaAuthService: OktaAuthService
    ) {
        // Create the OktaAuth object that handles tokens
        this.oktaConfig = this.oktaAuthService.getOktaConfig();
        this.oktaAuth = new OktaAuth(this.oktaConfig);
        this.oktaAuth.userAgent = `CID Hub ${this.oktaAuth.userAgent}`;
    }

    public async authGuardCanActivate() {
        // This is a simplified flow to get things working (see below
        // explanation)
        const isOktaAuthenticated = await this.isOktaAuthenticated();
        if (!isOktaAuthenticated) {
            // Clear the cache
            Utils.clearLocalStorage();
            await this.redirectToAgilentLoginPage();
            return false;
        }

        return true;

        // Below is the official flow for Agilent Okta login, but we can't
        // do this because fetch won't include the cookie with its request
        // to the Enterprise IT App session endpoint, so the function
        // this.isAgilentAuthenticated always returns false.
        // const isAgilentAuthenticated = await this.isAgilentAuthenticated();
        // if (!isAgilentAuthenticated) {
        //     await this.redirectToAgilentLoginPage();
        //     return false;
        // }
        // const isOktaAuthenticated = await this.isOktaAuthenticated();
        // if (!isOktaAuthenticated) {
        //     await this.redirectToOktaAuthorize();
        //     return false;
        // }

        // return true;
    }

    public async isOktaAuthenticated() {
        return await this.oktaAuthService.isAuthenticated();
    }

    public async redirectToAgilentLoginPage() {
        const loginParams = await this.prepareLoginParams();

        // Redirect to the Agilent login URL
        window.location.href = this.getLoginUrl(loginParams);
    }

    public async isAgilentAuthenticated() {
        // Expected response:
        // { "agsessionme": "true" }
        const response = await fetch(
            this.config.sessionUrl,
            {
                cache: 'no-cache',
                credentials: 'include'
            }
        );

        if (response.ok) {
            try {
                const respData = await response.json();
                if (respData['agsessionme'] === 'true') {
                    return true;
                }
            }
            catch (ex) {
                const respText = await response.text();
                console.log('Agilent session response error', respText, ex);
            }
        }
        return false;
    }

    public async redirectToOktaAuthorize() {
        // This function redirects to the Okta login page, which
        // will redirect straight back to the app if the user
        // is already signed in to Okta.
        await this.oktaAuth.token.getWithRedirect({
            scopes: this.oktaConfig.scopes,
            responseType: this.oktaConfig.responseType
        });
    }

    public async getAccessToken() {
        return await this.oktaAuthService.getAccessToken();
    }

    public async logout() {
        const loginParams = await this.prepareLoginParams();

        // Redirect to the Agilent logout URL
        window.location.href = this.getLogoutUrl(loginParams);
    }

    public async refreshAuthToken() {
        // Agilent Okta authentication doesn't allow the user to
        // refresh the auth token in the background. Instead, the
        // user is redirected to the session timeout login page.
        const loginParams = await this.prepareLoginParams();

        // Redirect to the Agilent session time-out URL
        window.location.href = this.getSessionTimeoutUrl(loginParams);
    }

    private async prepareLoginParams(): Promise<OktaLoginParams> {
        // This function uses the oktaAuth.token.getWithRedirect function to
        // generate the necessary fields and save them to session storage,
        // in preparation for redirecting to the login page.

        // Override the internal _setLocation function on the oktaAuth.token
        // object to grab the loginUrl with its query string instead of
        // redirecting there
        let loginUrlStr = null;
        this.oktaAuth.token.getWithRedirect._setLocation = (e) => {
            loginUrlStr = e;
        };

        // Call the getWithRedirect function to generate the login fields
        // and set loginUrlStr
        await this.oktaAuth.token.getWithRedirect({
            scopes: this.oktaConfig.scopes,
            responseType: this.oktaConfig.responseType
        });

        // Extract the state, challenge, nonce and redirect_uri from loginUrlStr
        // and save them in the params object.
        //
        // Example params object:
        // {
        //     "client_id": "0oaxa1i9a38QZqr1p0h7",
        //     "code_challenge": "SL_1yErykPg5iyhgogMvjbMDRn1lDoD5z4qR9gBTwuU",
        //     "code_challenge_method": "S256",
        //     "nonce": "5dIuI7kldUFdw0fJM0E8Tnwit1zP3VwSfn0MKAukn0ueWFn9YBBll5GZEZxeNobv",
        //     "redirect_uri": "http://localhost:4200/login/callback",
        //     "response_type": "code",
        //     "state": "TLaIJTV4nfaLqxDMhfa4fEHKtSYYtV9PBOEZVDZEgU9FStRljUUTZ5XEeciJp5Jt",
        //     "scope": "openid profile email"
        // }
        const loginParams = {};
        const loginUrl = new URL(loginUrlStr);
        for (const p of loginUrl.searchParams.entries()) {
            loginParams[p[0]] = p[1];
        }
        return loginParams as OktaLoginParams;
    }

    private getLoginUrl(loginParams) {
        return this.getLoginParamsUrl(this.config.loginUrl, loginParams);
    }

    private getLogoutUrl(loginParams) {
        return this.getLoginParamsUrl(this.config.logoutUrl, loginParams);
    }

    private getSessionTimeoutUrl(loginParams) {
        return this.getLoginParamsUrl(
            this.config.logoutUrl,
            loginParams,
            { lgt: '1' });
    }

    private getLoginParamsUrl(url, loginParams, extraParams = {}) {
        // Example Agilent login URL:
        // https://dev-register.agilent.com/account/login?
        // state=2df4c3d8b52a4d75a7e1d4e711f2391b
        // &challenge=5c0698beb3f9f33e92e5c0be248b17631e7da0bd74dfa6c181772dc4c6799ac3
        // &nonce=41543f4bd8e143b18efa752f99294f92
        // &aid=ACP
        // &redirect_uri=http%3A%2F%2Flocalhost%3A4200%2Flogin%2Fcallback
        const urlObj = new URL(url);
        urlObj.searchParams.append('state', loginParams.state);
        urlObj.searchParams.append('challenge', loginParams.code_challenge);
        urlObj.searchParams.append('nonce', loginParams.nonce);
        urlObj.searchParams.append('aid', this.config.appId);
        urlObj.searchParams.append('redirect_uri', loginParams.redirect_uri);

        for (const key in extraParams) {
            urlObj.searchParams.append(key, extraParams[key]);
        }

        return urlObj.href;
    }

}
